/**
 * Reimbursements API (TMMAPP-0031 / TMMAPI-0064) — candidates, list, link / confirm / reject /
 * unlink. Wire types from `src/api/generated/`; normalizers keep `grade` and `confidence`. A
 * candidate's served `reason` is English prose, so it isn't normalized and can't be rendered
 * (TMM-B3, TMMAPP-0122): the match chip reads the grade. A link carries `reasonCode` (TMMAPI-0157),
 * and a WITHDRAWN link is never listed. Confirm is the only money-moving write (server allocation); unlink reverses.
 */
import { request } from './client';
import { ApiShapeError } from './errors';
import type {
  ReimbursementCandidateView as WireCandidate,
  ReimbursementCandidatesResponse as WireCandidatesResponse,
  ReimbursementConfirmRequest as WireConfirmRequest,
  ReimbursementCreditView as WireCredit,
  ReimbursementLinkRequest as WireLinkRequest,
  ReimbursementLinkView as WireLink,
  ReimbursementListResponse as WireListResponse,
} from './generated';

export type ReimbursementGrade = 'EXACT' | 'FRACTION' | 'NEAR';
export type ReimbursementConfidence = 'HIGH' | 'MEDIUM';
export type ReimbursementStatus = 'PROPOSED' | 'CONFIRMED' | 'REJECTED' | 'WITHDRAWN';
/** Why a link was made (TMMAPI-0157): the match's grade, or `MANUAL` for one the person made. */
export type ReimbursementReasonCode = 'EXACT' | 'FRACTION' | 'NEAR' | 'MANUAL';

export interface ReimbursementCredit {
  transactionId: number;
  accountId: number;
  postedOn: string;
  payee: string;
  amountMinor: number;
  currency: string;
}

export interface ReimbursementCandidate {
  expenseTransactionId: number;
  postedOn: string;
  dayGap: number;
  payee: string;
  amountMinor: number;
  categoryName: string | null;
  envelopeId: number | null;
  grade: ReimbursementGrade;
  confidence: ReimbursementConfidence;
}

export interface ReimbursementCandidatesResult {
  credit: ReimbursementCredit;
  candidates: ReimbursementCandidate[];
}

export interface ReimbursementLink {
  id: number;
  status: ReimbursementStatus;
  grade: ReimbursementGrade;
  confidence: ReimbursementConfidence;
  /** Replaces the prose `reason` (TMMAPI-0157); null for a code this build doesn't know. */
  reasonCode: ReimbursementReasonCode | null;
  /** The n of a FRACTION match ("half" is 2), as served; null otherwise. */
  fractionDenominator: number | null;
  expenseTransactionId: number;
  creditTransactionId: number;
  /** Owning account of the expense. */
  expenseAccountId: number;
  /** Owning account of the credit. */
  creditAccountId: number;
  offsetAllocationId: number | null;
  createdAt: string;
  decidedAt: string | null;
}

export interface ReimbursementListPage {
  items: ReimbursementLink[];
  nextCursor: string | null;
}

export interface ReimbursementLinkBody {
  expenseTransactionId: number;
  creditTransactionId: number;
  amountMinor?: number;
}

export interface ReimbursementConfirmBody {
  envelopeId?: number;
  amountMinor?: number;
}

const GRADES: ReadonlySet<string> = new Set(['EXACT', 'FRACTION', 'NEAR']);
const CONFIDENCES: ReadonlySet<string> = new Set(['HIGH', 'MEDIUM']);
const STATUSES: ReadonlySet<string> = new Set(['PROPOSED', 'CONFIRMED', 'REJECTED', 'WITHDRAWN']);
const REASON_CODES: ReadonlySet<string> = new Set(['EXACT', 'FRACTION', 'NEAR', 'MANUAL']);

export function normalizeReimbursementCredit(w: WireCredit): ReimbursementCredit {
  return {
    transactionId: w.transactionId,
    accountId: w.accountId,
    postedOn: w.postedOn,
    payee: w.payee ?? '',
    amountMinor: w.amountMinor,
    currency: w.currency,
  };
}

/** A candidate; `null` (dropped) for a grade or confidence this build doesn't know. */
export function normalizeReimbursementCandidate(w: WireCandidate): ReimbursementCandidate | null {
  if (!GRADES.has(w.grade) || !CONFIDENCES.has(w.confidence)) return null;
  return {
    expenseTransactionId: w.expenseTransactionId,
    postedOn: w.postedOn,
    dayGap: w.dayGap,
    payee: w.payee ?? '',
    amountMinor: w.amountMinor,
    categoryName: w.categoryName,
    envelopeId: w.envelopeId,
    grade: w.grade,
    confidence: w.confidence,
  };
}

export function normalizeReimbursementCandidates(
  w: WireCandidatesResponse,
): ReimbursementCandidatesResult {
  return {
    credit: normalizeReimbursementCredit(w.credit),
    candidates: w.candidates.flatMap((row) => {
      const item = normalizeReimbursementCandidate(row);
      return item ? [item] : [];
    }),
  };
}

/** A link; `null` (dropped) for a status, grade or confidence this build doesn't know. */
export function normalizeReimbursementLink(w: WireLink): ReimbursementLink | null {
  if (!STATUSES.has(w.status) || !GRADES.has(w.grade) || !CONFIDENCES.has(w.confidence)) {
    return null;
  }
  return {
    id: w.id,
    status: w.status,
    grade: w.grade,
    confidence: w.confidence,
    reasonCode: REASON_CODES.has(w.reasonCode) ? w.reasonCode : null,
    fractionDenominator: w.fractionDenominator,
    expenseTransactionId: w.expenseTransactionId,
    creditTransactionId: w.creditTransactionId,
    expenseAccountId: w.expenseAccountId,
    creditAccountId: w.creditAccountId,
    offsetAllocationId: w.offsetAllocationId,
    createdAt: w.createdAt,
    decidedAt: w.decidedAt,
  };
}

export function normalizeReimbursementList(w: WireListResponse): ReimbursementListPage {
  // A WITHDRAWN link (one side became a transfer, TMMAPI-0157) is never listed or counted.
  const items = w.items.flatMap((row) => {
    const item = normalizeReimbursementLink(row);
    return item && item.status !== 'WITHDRAWN' ? [item] : [];
  });
  return { items, nextCursor: w.nextCursor };
}

function requiredLink(wire: WireLink): ReimbursementLink {
  const link = normalizeReimbursementLink(wire);
  if (!link) throw new ApiShapeError('reimbursement status, grade or confidence is unknown');
  return link;
}

export const reimbursementsApi = {
  /** GET /v1/me/reimbursements/candidates?transactionId&windowDays */
  async candidates(
    transactionId: number,
    windowDays?: number,
  ): Promise<ReimbursementCandidatesResult> {
    return normalizeReimbursementCandidates(
      await request<WireCandidatesResponse>('GET', '/v1/me/reimbursements/candidates', {
        query: { transactionId, windowDays },
      }),
    );
  },

  /** GET /v1/me/reimbursements?status&cursor */
  async list(status?: ReimbursementStatus, cursor?: string): Promise<ReimbursementListPage> {
    return normalizeReimbursementList(
      await request<WireListResponse>('GET', '/v1/me/reimbursements', {
        query: { status, cursor },
      }),
    );
  },

  /** POST /v1/me/reimbursements — manual link (does not move money). */
  async link(body: ReimbursementLinkBody): Promise<ReimbursementLink> {
    const json: WireLinkRequest = {
      expenseTransactionId: body.expenseTransactionId,
      creditTransactionId: body.creditTransactionId,
    };
    if (body.amountMinor !== undefined) json.amountMinor = body.amountMinor;
    return requiredLink(await request<WireLink>('POST', '/v1/me/reimbursements', { json }));
  },

  /** POST /v1/me/reimbursements/{id}/confirm — writes the offsetting allocation. */
  async confirm(id: number, body?: ReimbursementConfirmBody): Promise<ReimbursementLink> {
    const json: WireConfirmRequest = {};
    if (body?.amountMinor !== undefined) json.amountMinor = body.amountMinor;
    if (body?.envelopeId !== undefined) json.envelopeId = body.envelopeId;
    return requiredLink(
      await request<WireLink>('POST', `/v1/me/reimbursements/${id}/confirm`, { json }),
    );
  },

  /** POST /v1/me/reimbursements/{id}/reject */
  async reject(id: number): Promise<ReimbursementLink> {
    return requiredLink(await request<WireLink>('POST', `/v1/me/reimbursements/${id}/reject`));
  },

  /** POST /v1/me/reimbursements/{id}/unlink — reverses the offset; never deletes (MM-D-19). */
  async unlink(id: number): Promise<ReimbursementLink> {
    return requiredLink(await request<WireLink>('POST', `/v1/me/reimbursements/${id}/unlink`));
  },
};
